import { jwtDecode, type JwtPayload } from "jwt-decode";
import * as Sentry from "@sentry/vue";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useSelectedCustomerStore } from "@/shared/stores/SelectedCustomerStore";
import type { OciTransferMetaData } from "@/shared/@types/OciTransferMetaData";
import { RoleActions } from "@/shared/@types/RoleActions";
import { usePermission } from "@/shared/composables/usePermission.composable";
import {
  OCI_TRANSFER_METADATA_DEFAULTS,
  OCI_TRANSFER_METADATA_STORAGE_KEY,
} from "@/shared/auth/helper/authConstants.helper";
import {
  clearAuthTokensCookie,
  setAuthTokensCookie,
} from "@/shared/auth/helper/authTokensCookie.helper";

type OciJwtClaims = JwtPayload & {
  access_token?: string;
  hook_url?: string;
  id_token?: string;
  refresh_token?: string;
};

// Extract form metadata from the OCI hook URL if present. Otherwise use defaults.
const parseOciTransferMetaData = (hookUrl: string): OciTransferMetaData => {
  const url = new URL(hookUrl);

  return {
    hookUrl,
    okCode:
      url.searchParams.get("~OKCode") || OCI_TRANSFER_METADATA_DEFAULTS.okCode,
    caller:
      url.searchParams.get("~caller") || OCI_TRANSFER_METADATA_DEFAULTS.caller,
    // The OCI 4.0 specification defines "returntarget" as the parameter for the target frame, but some implementations use "~target", so we check for both parameters with a fallback to the default value
    target:
      url.searchParams.get("returntarget") ||
      url.searchParams.get("~target") ||
      OCI_TRANSFER_METADATA_DEFAULTS.target,
  };
};

// Read OCI metadata from the session storage and return a safe, normalized object
const getOciTransferMetaDataFromStorage = () => {
  if (typeof window === "undefined") return undefined;

  const value = window.sessionStorage.getItem(
    OCI_TRANSFER_METADATA_STORAGE_KEY,
  );
  if (!value) return undefined;

  try {
    const parsedMetaData = JSON.parse(value) as OciTransferMetaData;
    if (!parsedMetaData.hookUrl) return undefined;

    return {
      hookUrl: parsedMetaData.hookUrl,
      okCode: parsedMetaData.okCode || OCI_TRANSFER_METADATA_DEFAULTS.okCode,
      caller: parsedMetaData.caller || OCI_TRANSFER_METADATA_DEFAULTS.caller,
      target: parsedMetaData.target || OCI_TRANSFER_METADATA_DEFAULTS.target,
    };
  } catch {
    return undefined;
  }
};

// Parse hash fragment parameters (e.g. #jwt=...) from the current URL
const getHashParams = (hash: string) => {
  return new URLSearchParams(hash.replace(/^#/, ""));
};

// Add some security by ensuring that the provided token is decodable before trusting its claims and persisting it in a session cookie
const canDecodeJwt = (token: string) => {
  try {
    jwtDecode(token);
    return true;
  } catch {
    return false;
  }
};

// Log errors and reasons for OCI session initialization failures to Sentry
const reportOciSessionInitializationFailure = (
  reason: string,
  extra?: Record<string, unknown>,
) => {
  Sentry.captureMessage("OCI session initialization failed", {
    extra: {
      reason,
      ...extra,
    },
  });

  return false;
};

export const useOciSession = () => {
  const selectedCustomerStore = useSelectedCustomerStore();
  const { customerRoles } = storeToRefs(selectedCustomerStore);

  const { hasPermission } = usePermission();

  // OCI login is present when the URL hash carries a `jwt` parameter
  const shouldInitializeOciSession = (hash: string) => {
    return getHashParams(hash).has("jwt");
  };

  // Initialize the session cookie and put the transfer metadata in the session storage. The data comes from the jwt claims in the hash.
  const initializeOciSession = (hash: string): boolean => {
    if (typeof window === "undefined") return false;

    const jwt = getHashParams(hash).get("jwt");
    if (!jwt) return reportOciSessionInitializationFailure("missing_jwt");

    try {
      const claims = jwtDecode<OciJwtClaims>(jwt);
      const accessToken = claims.access_token;
      const idToken = claims.id_token;
      const refreshToken = claims.refresh_token;
      const hookUrl = claims.hook_url;

      // All required claims must be present before creating an OCI session
      if (!accessToken || !idToken || !refreshToken || !hookUrl) {
        return reportOciSessionInitializationFailure("missing_required_claims");
      }

      // Ensure ID token is decodable before persisting auth state
      if (!canDecodeJwt(idToken)) {
        return reportOciSessionInitializationFailure("invalid_id_token");
      }

      // Clear any existing auth tokens to avoid conflicts with the OCI session tokens
      clearAuthTokensCookie();

      setAuthTokensCookie({
        idToken,
        accessToken,
        refreshToken,
      });

      const ociTransferMetaData = parseOciTransferMetaData(hookUrl);

      // Store metadata so it can be used when transferring the cart and to identify a current OCI session
      window.sessionStorage.setItem(
        OCI_TRANSFER_METADATA_STORAGE_KEY,
        JSON.stringify(ociTransferMetaData),
      );

      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  };

  const ociTransferMetaData = computed(() =>
    getOciTransferMetaDataFromStorage(),
  );

  // User is considered in OCI session only with metadata + dedicated OCI-only permission
  const isOciSession = computed(() => {
    return (
      !!ociTransferMetaData.value &&
      hasPermission(RoleActions.ACCESS_GAS_OCI_ONLY)
    );
  });

  // In theory, OCI customers can get auth tokens by logging in through our login form. In that case we need to identify them to prevent app access.
  const isOciCustomerWithoutSession = computed(() => {
    return (
      !ociTransferMetaData.value &&
      customerRoles.value.length === 1 &&
      hasPermission(RoleActions.ACCESS_GAS_OCI_ONLY)
    );
  });

  const clearOciSession = () => {
    // Clear auth tokens to prevent auth conflicts if the user comes back to the website after the OCI transfer
    clearAuthTokensCookie();

    // Clear session storage to remove any remaining OCI metadata that might be used in future sessions
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(OCI_TRANSFER_METADATA_STORAGE_KEY);
    }
  };

  return {
    isOciSession,
    ociTransferMetaData,
    isOciCustomerWithoutSession,
    shouldInitializeOciSession,
    initializeOciSession,
    clearOciSession,
  };
};
